import { RefObject, useLayoutEffect, useState } from 'react';

/** How a one-line button label is changed to fit: letter spacing in px, font size as a share of the button's. */
export type LabelFit = { letterSpacing: number; fontScale: number };

const TIGHTEST_LETTER_SPACING = 1;
// The button is at most the screen width less 8px each side, and the label
// keeps at least 8px from each end of it.
const SCREEN_MARGINS = 16;
const INNER_MARGINS = 16;

/**
 * How to fit a label of `characters` characters whose glyphs take
 * `glyphWidth` px at the button's font size, set with `letterSpacing` px, into
 * `available` px: null while it fits as set, else first a tighter letter
 * spacing (down to 1px), and only then a smaller font size.
 */
export function fitLabel(
  glyphWidth: number,
  characters: number,
  letterSpacing: number,
  available: number,
): LabelFit | null {
  if (glyphWidth + characters * letterSpacing <= available) return null;
  const tightened = (available - glyphWidth) / characters;
  if (tightened >= TIGHTEST_LETTER_SPACING) return { letterSpacing: tightened, fontScale: 1 };
  return {
    letterSpacing: TIGHTEST_LETTER_SPACING,
    fontScale: (available - characters * TIGHTEST_LETTER_SPACING) / glyphWidth,
  };
}

const sameFit = (a: LabelFit | null, b: LabelFit | null) =>
  a === b ||
  (a !== null &&
    b !== null &&
    Math.abs(a.letterSpacing - b.letterSpacing) < 0.01 &&
    Math.abs(a.fontScale - b.fontScale) < 0.001);

/** A computed length in px; 0 for a keyword such as `normal`. */
const pixels = (value: string) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Keeps a one-line button label inside the widest the button may grow to. It
 * reads the label as laid out (its width, and the letter spacing and font
 * size it has now) against the button's own, so a larger system text size, a
 * fallback font or a fit already applied are all taken into account. It reads
 * again whenever the label or the window changes size. Returns null while the
 * label fits as the button sets it.
 */
export function useFitButtonLabel(
  labelRef: RefObject<HTMLElement | null>,
  text: string,
): LabelFit | null {
  const [fit, setFit] = useState<LabelFit | null>(null);

  useLayoutEffect(() => {
    const label = labelRef.current;
    const button = label?.parentElement;
    if (!label || !button) return undefined;
    const refit = () => {
      const width = label.getBoundingClientRect().width;
      const labelStyle = getComputedStyle(label);
      const buttonStyle = getComputedStyle(button);
      const labelFontSize = pixels(labelStyle.fontSize);
      const buttonFontSize = pixels(buttonStyle.fontSize);
      if (width <= 0 || labelFontSize <= 0 || buttonFontSize <= 0) return; // not laid out
      const glyphWidth =
        ((width - text.length * pixels(labelStyle.letterSpacing)) * buttonFontSize) / labelFontSize;
      const next = fitLabel(
        glyphWidth,
        text.length,
        pixels(buttonStyle.letterSpacing),
        window.innerWidth - SCREEN_MARGINS - INNER_MARGINS,
      );
      setFit((current) => (sameFit(current, next) ? current : next));
    };
    refit();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(refit);
    observer?.observe(label);
    window.addEventListener('resize', refit);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', refit);
    };
  }, [labelRef, text]);

  return fit;
}
